/* eslint-disable @wyz/module/no-relative-import */
import { LocalStorageStore, usePersistedState } from '@wyz/utils';
import * as React from 'react';
import { useManageUsers } from '~/common/api';
import { useApplicationContext } from '~/common/context';
import {
  IEditableFunctionalContextState,
  IFunctionalContext,
} from '~/purchase/types';
import { FunctionalContext } from './contexts';
import { usePurchase } from './purchase-provider';

const KEY = '__WYZ_PURCHASE_STATE__';
export function FunctionalProvider(props: React.PropsWithChildren) {
  const purchaseContext = usePurchase();
  const { auth } = useApplicationContext();
  const [contextState, setContext] =
    usePersistedState<IEditableFunctionalContextState>(
      KEY,
      {
        displayAdverts: true,
        selectedPrimaryCustomerId: undefined,
        selectedSecondaryCustomerId: undefined,
      },
      LocalStorageStore,
    );

  const managePrimaryUser = useManageUsers({
    getUserCustomerById: {
      args: {
        userId: auth.userId as string,
        customerId: contextState.selectedPrimaryCustomerId || '',
      },
      enabled: !!contextState.selectedPrimaryCustomerId && !!auth.userId,
      refetchOnMount: true,
    },
  });
  const manageSecondaryUser = useManageUsers({
    getUserCustomerById: {
      args: {
        userId: auth.userId as string,
        customerId: contextState.selectedSecondaryCustomerId || '',
      },
      enabled: !!contextState.selectedSecondaryCustomerId && !!auth.userId,
      refetchOnMount: true,
    },
  });

  const context: IFunctionalContext = React.useMemo(() => {
    const primaryCustomer = managePrimaryUser.getUserCustomerById.data?.data;
    const secondaryCustomer =
      manageSecondaryUser.getUserCustomerById.data?.data;
    return {
      state: {
        ...contextState,
        primaryCustomer,
        secondaryCustomer,
        targetCustomer: purchaseContext.isCallCenterMode
          ? secondaryCustomer
          : primaryCustomer,
        loading: {
          isLoadingPrimaryCustomer:
            managePrimaryUser.getUserCustomerById.isProcessing,
          isLoadingSecondaryCustomer:
            manageSecondaryUser.getUserCustomerById.isProcessing,
        },
      },
      handlers: {
        setPrimaryCustomerId: (customerId) => {
          setContext((prev) => ({
            ...prev,
            selectedPrimaryCustomerId: customerId,
          }));
        },
        setSecondaryCustomerId: (customerId) => {
          setContext((prev) => ({
            ...prev,
            selectedSecondaryCustomerId: customerId,
          }));
        },
        clearSelectedCustomers: () => {
          setContext((prev) => ({
            ...prev,
            selectedPrimaryCustomerId: undefined,
            selectedSecondaryCustomerId: undefined,
          }));
        },
        showAdverts: () => {
          setContext((prev) => ({ ...prev, displayAdverts: true }));
        },
        hideAdverts: () => {
          setContext((prev) => ({ ...prev, displayAdverts: false }));
        },
      },
      isCallCenterMode: purchaseContext.isCallCenterMode,
      mode: purchaseContext.mode,
    };
  }, [
    contextState,
    managePrimaryUser.getUserCustomerById.data?.data,
    managePrimaryUser.getUserCustomerById.isProcessing,
    manageSecondaryUser.getUserCustomerById.data?.data,
    manageSecondaryUser.getUserCustomerById.isProcessing,
    purchaseContext.isCallCenterMode,
    purchaseContext.mode,
    setContext,
  ]);

  return (
    <FunctionalContext.Provider value={context}>
      {props.children}
    </FunctionalContext.Provider>
  );
}

export const usePurchaseContext = () => {
  const context = React.useContext(FunctionalContext);
  if (context === undefined) {
    throw new Error(
      'usePurchaseContext must be used within a FunctionalContext',
    );
  }
  return context;
};
